/**@format */
import { AnyAction } from '@reduxjs/toolkit';
import { assign } from '../../helpers/object';
import * as types from './actions';
import { List } from '../../types';
import _ from 'lodash';

type CommonError = null | Error | string;

type Common = {
  lists: List[];
  error: CommonError;
  loading: boolean;
  openedLists: List[];
  activeList: List | null;
  creating: boolean;
  selectAll: boolean;
  delete: {
    loading: boolean;
    error: CommonError;
  };
  update: {
    loading: boolean;
    error: CommonError;
  };
  addContact: {
    loading: boolean;
    error: CommonError;
  };
  removeContact: {
    loading: boolean;
    error: CommonError;
  };
  addUser: {
    loading: boolean;
    error: CommonError;
  };
  removeUser: {
    loading: boolean;
    error: CommonError;
  };
};

type InitalState = {
  people: Common;
  organizations: Common;
};

const initalState: InitalState = {
  people: {
    selectAll: false,
    lists: [],
    error: null,
    loading: false,
    openedLists: [],
    activeList: null,
    creating: false,
    delete: {
      loading: false,
      error: null,
    },
    update: {
      loading: false,
      error: null,
    },
    addContact: {
      loading: false,
      error: null,
    },
    removeContact: {
      loading: false,
      error: null,
    },
    addUser: {
      loading: false,
      error: null,
    },
    removeUser: {
      loading: false,
      error: null,
    },
  },
  organizations: {
    selectAll: false,
    lists: [],
    error: null,
    loading: false,
    openedLists: [],
    activeList: null,
    creating: false,
    delete: {
      loading: false,
      error: null,
    },
    update: {
      loading: false,
      error: null,
    },
    addContact: {
      loading: false,
      error: null,
    },
    removeContact: {
      loading: false,
      error: null,
    },
    addUser: {
      loading: false,
      error: null,
    },
    removeUser: {
      loading: false,
      error: null,
    },
  },
};

export default (
  state: InitalState = initalState,
  { type, payload }: AnyAction
): InitalState => {
  switch (type) {
    case types.LOAD_PEOPLE_LISTS_REQUEST: {
      return assign(state, {
        people: assign(state.people, {
          loading: true,
        }),
      });
    }
    case types.LOAD_PEOPLE_LISTS_SUCCESS: {
      return assign(state, {
        people: assign(state.people, {
          loading: false,
          error: null,
          lists: payload,
        }),
      });
    }
    case types.LOAD_PEOPLE_LISTS_ERROR: {
      return {
        ...state,
        people: {
          ...state.people,
          loading: false,
          error: payload.error,
          lists: [],
        },
      };
    }
    case types.LOAD_PEOPLE_LIST_SUCCESS: {
      const lists = [...state.people.lists];
      const index = lists.findIndex(list => list.id === payload.list.id);
      const openedLists = state.people.openedLists.map(list => {
        if (list.id !== payload.list.id) return list;
        return payload.list;
      });
      const activeList =
        state.people.activeList === null
          ? null
          : state.people.activeList.id === payload.list.id
          ? payload.list
          : state.people.activeList;
      if (index >= 0) {
        lists[index] = payload.list;
      } else {
        lists.push(payload.list);
      }
      return {
        ...state,
        people: {
          ...state.people,
          lists,
          activeList,
          openedLists,
        },
      };
    }
    case types.CREATE_PEOPLE_LIST_SUCCESS: {
      const copy = [...state.people.lists];
      if (payload.list) {
        copy.push(payload.list);
      }
      return assign(state, {
        people: assign(state.people, {
          lists: copy,
        }),
      });
    }
    case types.UPDATE_PEOPLE_LIST_REQUEST: {
      return {
        ...state,
        people: {
          ...state.people,
          update: {
            loading: true,
            error: null,
          },
        },
      };
    }
    case types.UPDATE_PEOPLE_LIST_SUCCESS: {
      const copy = [...state.people.lists];
      let openedLists = [...state.people.openedLists];
      let activeList = state.people.activeList
        ? { ...state.people.activeList }
        : null;
      if (payload.list) {
        const index = copy.findIndex(list => list.id === payload.list.id);
        const mergedList =
          index >= 0 ? _.merge({}, copy[index], payload.list) : payload.list;
        if (index >= 0) {
          copy[index] = mergedList;
        } else {
          copy.push(payload.list);
        }

        if (activeList?.id === payload.list.id) {
          activeList = mergedList;
        }
        openedLists = openedLists.map(list => {
          if (list.id !== payload.list.id) return list;
          return mergedList;
        });
      }
      return {
        ...state,
        people: {
          ...state.people,
          lists: copy,
          activeList,
          openedLists,
          update: {
            loading: false,
            error: null,
          },
        },
      };
    }
    case types.UPDATE_PEOPLE_LIST_ERROR: {
      return {
        ...state,
        people: {
          ...state.people,
          update: {
            loading: false,
            error: payload.error,
          },
        },
      };
    }
    case types.ADD_PEOPLE_LIST_TO_OPENED_LISTS: {
      const exists = state.people.openedLists.find(
        l => l.id === payload.list.id
      );
      if (exists) return state;
      return assign(state, {
        people: assign(state.people, {
          openedLists: [...state.people.openedLists, payload.list],
        }),
      });
    }
    case types.REMOVE_PEOPLE_LIST_FROM_OPENED_LISTS: {
      const targetListId =
        typeof payload.list === 'object' ? payload.list.id : payload.list;
      const indexOfRemovedList = state.people.openedLists.findIndex(
        list => list.id === targetListId
      );
      const openedLists = state.people.openedLists.filter(
        list => list.id !== targetListId
      );

      let activeList = state.people.activeList
        ? { ...state.people.activeList }
        : null;
      if (activeList && activeList.id === targetListId) {
        if (openedLists.length === 0) {
          activeList = null;
        } else {
          const siblingIndex =
            indexOfRemovedList - 1 >= 0
              ? indexOfRemovedList - 1
              : indexOfRemovedList + 1;
          activeList = openedLists[siblingIndex];
        }
      }
      return assign(state, {
        people: assign(state.people, {
          openedLists,
          activeList,
        }),
      });
    }
    case types.SET_ACTIVE_PEOPLE_LIST: {
      let list = payload.list;
      // meaning list id was given
      // find the list based on id
      if (typeof list === 'string') {
        list = state.people.lists.find(l => l.id === list);
      }

      if (list && list.id === state.people.activeList?.id) return state;
      return assign(state, {
        people: assign(state.people, {
          activeList: list,
        }),
      });
    }

    case types.TOGGLE_SELECT_ALL_PEOPLE_CONTACT: {
      return assign(state, {
        people: assign(state.people, {
          selectAll: payload,
        }),
      });
    }

    case types.ADD_CONTACT_TO_PEOPLE_LIST_REQUEST: {
      return assign(state, {
        people: assign(state.people, {
          addContact: {
            loading: true,
            error: null,
          },
        }),
      });
    }
    case types.ADD_CONTACT_TO_PEOPLE_LIST_SUCCESS: {
      return assign(state, {
        people: assign(state.people, {
          addContact: {
            loading: false,
            error: null,
          },
        }),
      });
    }
    case types.ADD_CONTACT_TO_PEOPLE_LIST_ERROR: {
      return assign(state, {
        people: assign(state.people, {
          addContact: {
            loading: false,
            error: payload.error,
          },
        }),
      });
    }

    case types.REMOVE_CONTACT_FROM_PEOPLE_LIST_REQUEST: {
      return assign(state, {
        people: assign(state.people, {
          removeContact: {
            loading: true,
            error: null,
          },
        }),
      });
    }
    case types.REMOVE_CONTACT_FROM_PEOPLE_LIST_SUCCESS: {
      return assign(state, {
        people: assign(state.people, {
          removeContact: {
            loading: false,
            error: null,
          },
        }),
      });
    }
    case types.REMOVE_CONTACT_FROM_PEOPLE_LIST_ERROR: {
      return assign(state, {
        people: assign(state.people, {
          removeContact: {
            loading: false,
            error: payload.error,
          },
        }),
      });
    }

    case types.DELETE_PEOPLE_LIST_REQUEST: {
      return {
        ...state,
        people: {
          ...state.people,
          delete: {
            error: null,
            loading: true,
          },
        },
      };
    }
    case types.DELETE_PEOPLE_LIST_SUCCESS: {
      const lists = state.people.lists.filter(l => l.id !== payload.id);
      const openedLists = state.organizations.openedLists.filter(
        l => l.id !== payload.id
      );
      const activeList =
        state.organizations.activeList?.id === payload.id
          ? null
          : state.organizations.activeList;
      return {
        ...state,
        people: {
          ...state.people,
          lists,
          openedLists,
          activeList,
          delete: {
            error: null,
            loading: false,
          },
        },
      };
    }
    case types.DELETE_PEOPLE_LIST_ERROR: {
      return {
        ...state,
        people: {
          ...state.people,
          delete: {
            error: payload.error,
            loading: false,
          },
        },
      };
    }
    case types.ADD_USER_TO_PEOPLE_LIST_REQUEST: {
      return {
        ...state,
        people: {
          ...state.people,
          addUser: {
            error: null,
            loading: true,
          },
        },
      };
    }
    // handle both cases in one block. Set error
    // if passed otherwise null and set loading to false
    case types.ADD_USER_TO_PEOPLE_LIST_SUCCESS:
    case types.ADD_USER_TO_PEOPLE_LIST_ERROR: {
      return {
        ...state,
        people: {
          ...state.people,
          addUser: {
            error: payload?.error || null,
            loading: false,
          },
        },
      };
    }

    // -----------------
    // org contact section start
    // ----------------

    case types.LOAD_ORGANIZATION_LISTS_REQUEST: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          loading: true,
        },
      };
    }
    case types.LOAD_ORGANIZATION_LISTS_SUCCESS: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          loading: false,
          error: null,
          lists: payload,
        },
      };
    }
    case types.LOAD_ORGANIZATION_LISTS_ERROR: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          loading: false,
          error: payload.error,
          lists: [],
        },
      };
    }
    case types.LOAD_ORGANIZATION_LIST_SUCCESS: {
      const lists = [...state.organizations.lists];
      const index = lists.findIndex(list => list.id === payload.list.id);
      const openedLists = state.organizations.openedLists.map(list => {
        if (list.id !== payload.list.id) return list;
        return payload.list;
      });
      const activeList =
        state.organizations.activeList === null
          ? null
          : state.organizations.activeList.id === payload.list.id
          ? payload.list
          : state.organizations.activeList;
      if (index >= 0) {
        lists[index] = payload.list;
      } else {
        lists.push(payload.list);
      }
      return {
        ...state,
        organizations: {
          ...state.organizations,
          openedLists,
          activeList,
          lists,
        },
      };
    }
    case types.CREATE_ORGANIZATION_LIST_SUCCESS: {
      const copy = [...state.organizations.lists];
      if (payload.list) {
        copy.push(payload.list);
      }
      return assign(state, {
        organizations: assign(state.organizations, {
          lists: copy,
        }),
      });
    }
    case types.UPDATE_ORGANIZATION_LIST_REQUEST: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          update: {
            loading: true,
            error: null,
          },
        },
      };
    }
    case types.UPDATE_ORGANIZATION_LIST_SUCCESS: {
      const lists = [...state.organizations.lists];
      let activeList = state.organizations.activeList
        ? { ...state.organizations.activeList }
        : null;
      let openedLists = [...state.organizations.openedLists];
      if (payload.list) {
        const index = lists.findIndex(list => list.id === payload.list.id);
        const mergedList =
          index >= 0 ? _.merge({}, lists[index], payload.list) : payload.list;

        if (index >= 0) {
          lists[index] = mergedList;
        } else {
          lists.push(payload.list);
        }
        if (activeList?.id === payload.list.id) {
          activeList = mergedList;
        }
        openedLists = openedLists.map(list => {
          if (list.id !== payload.list.id) return list;
          return mergedList;
        });
      }
      return {
        ...state,
        organizations: {
          ...state.organizations,
          lists: lists,
          activeList,
          openedLists,
          update: {
            loading: false,
            error: null,
          },
        },
      };
    }
    case types.UPDATE_ORGANIZATION_LIST_ERROR: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          update: {
            loading: false,
            error: payload.error,
          },
        },
      };
    }
    case types.ADD_ORGANIZATIONS_LIST_TO_OPENED_LISTS: {
      const exists = state.organizations.openedLists.find(
        l => l.id === payload.list.id
      );
      if (exists) return state;
      return assign(state, {
        organizations: assign(state.organizations, {
          openedLists: [...state.organizations.openedLists, payload.list],
        }),
      });
    }
    case types.REMOVE_ORGANIZATIONS_LIST_FROM_OPENED_LISTS: {
      const targetListId =
        typeof payload.list === 'object' ? payload.list.id : payload.list;
      const isActive =
        state.organizations.activeList &&
        state.organizations.activeList.id === targetListId;
      const activeList = isActive ? null : state.organizations.activeList;
      return assign(state, {
        organizations: assign(state.organizations, {
          openedLists: state.organizations.openedLists.filter(
            list => list.id !== targetListId
          ),
          activeList,
        }),
      });
    }
    case types.SET_ACTIVE_ORGANIZATIONS_LIST: {
      let list = payload.list;
      // meaning list id was given
      // find the list based on id
      if (typeof list === 'string') {
        list = state.organizations.lists.find(l => l.id === list);
      }
      if (list && list.id === state.organizations.activeList?.id) return state;
      return assign(state, {
        organizations: assign(state.organizations, {
          activeList: list,
        }),
      });
    }
    case types.TOGGLE_SELECT_ALL_ORGANIZATION_CONTACT: {
      return assign(state, {
        organizations: assign(state.organizations, {
          selectAll: payload,
        }),
      });
    }

    case types.ADD_CONTACT_TO_ORGANIZATION_LIST_REQUEST: {
      return assign(state, {
        organizations: assign(state.organizations, {
          addContact: {
            loading: true,
            error: null,
          },
        }),
      });
    }
    case types.ADD_CONTACT_TO_ORGANIZATION_LIST_SUCCESS: {
      return assign(state, {
        organizations: assign(state.organizations, {
          addContact: {
            loading: false,
            error: null,
          },
        }),
      });
    }
    case types.ADD_CONTACT_TO_ORGANIZATION_LIST_ERROR: {
      return assign(state, {
        organizations: assign(state.organizations, {
          addContact: {
            loading: false,
            error: payload.error,
          },
        }),
      });
    }

    case types.REMOVE_CONTACT_FROM_ORGANIZATION_LIST_REQUEST: {
      return assign(state, {
        organizations: assign(state.organizations, {
          removeContact: {
            loading: true,
            error: null,
          },
        }),
      });
    }
    case types.REMOVE_CONTACT_FROM_ORGANIZATION_LIST_SUCCESS: {
      return assign(state, {
        organizations: assign(state.organizations, {
          removeContact: {
            loading: false,
            error: null,
          },
        }),
      });
    }
    case types.REMOVE_CONTACT_FROM_ORGANIZATION_LIST_ERROR: {
      return assign(state, {
        organizations: assign(state.organizations, {
          removeContact: {
            loading: false,
            error: payload.error,
          },
        }),
      });
    }

    case types.DELETE_ORGANIZATION_LIST_REQUEST: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          delete: {
            error: null,
            loading: true,
          },
        },
      };
    }
    case types.DELETE_ORGANIZATION_LIST_SUCCESS: {
      const lists = state.organizations.lists.filter(l => l.id !== payload.id);
      const openedLists = state.organizations.openedLists.filter(
        l => l.id !== payload.id
      );
      const activeList =
        state.organizations.activeList?.id === payload.id
          ? null
          : state.organizations.activeList;
      return {
        ...state,
        organizations: {
          ...state.organizations,
          lists,
          openedLists,
          activeList,
          delete: {
            error: null,
            loading: false,
          },
        },
      };
    }
    case types.DELETE_ORGANIZATION_LIST_ERROR: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          delete: {
            error: payload.error,
            loading: false,
          },
        },
      };
    }
    case types.ADD_USER_TO_ORGANIZATION_LIST_REQUEST: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          addUser: {
            error: null,
            loading: true,
          },
        },
      };
    }
    // handle both cases in one block. Set error
    // if passed otherwise null and set loading to false
    case types.ADD_USER_TO_ORGANIZATION_LIST_SUCCESS:
    case types.ADD_USER_TO_ORGANIZATION_LIST_ERROR: {
      return {
        ...state,
        organizations: {
          ...state.organizations,
          addUser: {
            error: payload?.error || null,
            loading: false,
          },
        },
      };
    }

    default:
      return state;
  }
};
